import React, { useRef, useState, useCallback, useEffect } from 'react';
import ArchitectureNode, { getNodeAnchor } from './ArchitectureNode';
import ArchitectureLink from './ArchitectureLink';
import { useEdgeCreation } from './hooks/useEdgeCreation';

/**
 * Canvas dimensions
 */
const CANVAS_WIDTH = 850;
const CANVAS_HEIGHT = 500;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const GRID_SIZE = 20;

/**
 * ArchitectureCanvas Component
 *
 * The main canvas area for the architecture diagram.
 * Supports pan, zoom, and renders nodes and edges.
 */
function ArchitectureCanvas({
  nodes,
  edges,
  viewport,
  selectedNodeId,
  selectedEdgeId,
  onSelectNode,
  onSelectEdge,
  onDeleteNode,
  onDeleteEdge,
  onAddEdge,
  onOpenSelector,
  onPan,
  onZoom,
  readOnly = false,
  getNodeById,
  // Vendor overlay props
  hasVendorOverlay = false,
  isNodeHighlighted,
  getNodeCoverage,
  getHighlightColor,
  getVendorLogo,
  getVendorProduct
}) {
  const containerRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Edge creation state
  const {
    isCreating: isEdgeCreating,
    sourceNodeId: edgeSourceNodeId,
    sourceAnchor: edgeSourceAnchor,
    previewPoint,
    hoveredTarget,
    startCreating: startEdgeCreation,
    updatePreview: updateEdgePreview,
    setTargetHover,
    clearTargetHover,
    completeCreation: completeEdgeCreation,
    cancelCreation: cancelEdgeCreation
  } = useEdgeCreation(onAddEdge);

  /**
   * Handle mouse wheel for zooming
   */
  const handleWheel = useCallback((e) => {
    if (readOnly) return;
    e.preventDefault();

    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, viewport.zoom + delta));
    onZoom?.(newZoom);
  }, [viewport.zoom, onZoom, readOnly]);

  /**
   * Handle mouse down for panning
   */
  const handleMouseDown = useCallback((e) => {
    // Only pan on middle mouse button or when clicking on canvas background
    if (e.button === 1 || (e.button === 0 && e.target === e.currentTarget)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - viewport.x, y: e.clientY - viewport.y });
      e.preventDefault();
    }
  }, [viewport.x, viewport.y]);

  /**
   * Handle mouse move for panning and edge creation preview
   */
  const handleMouseMove = useCallback((e) => {
    // Edge creation preview
    if (isEdgeCreating && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Convert screen coords to canvas coords (accounting for viewport transform)
      const canvasX = (e.clientX - rect.left - viewport.x) / viewport.zoom;
      const canvasY = (e.clientY - rect.top - viewport.y) / viewport.zoom;
      updateEdgePreview({ x: canvasX, y: canvasY });
      return;
    }

    if (!isPanning) return;

    const newX = e.clientX - panStart.x;
    const newY = e.clientY - panStart.y;

    // Call onPan with delta
    onPan?.(newX - viewport.x, newY - viewport.y);
  }, [isPanning, panStart, viewport.x, viewport.y, viewport.zoom, onPan, isEdgeCreating, updateEdgePreview]);

  /**
   * Handle mouse up to stop panning or complete edge creation
   */
  const handleMouseUp = useCallback(() => {
    if (isEdgeCreating) {
      // If we're hovering over a valid target, complete the edge
      if (hoveredTarget) {
        completeEdgeCreation(hoveredTarget.nodeId, hoveredTarget.anchor);
      } else {
        // Otherwise cancel
        cancelEdgeCreation();
      }
      return;
    }
    setIsPanning(false);
  }, [isEdgeCreating, hoveredTarget, completeEdgeCreation, cancelEdgeCreation]);

  /**
   * Click on canvas background to deselect
   */
  const handleCanvasClick = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.classList.contains('arch-canvas__grid')) {
      onSelectNode?.(null);
      onSelectEdge?.(null);
    }
  }, [onSelectNode, onSelectEdge]);

  /**
   * Attach global mouse event listeners for panning and edge creation
   */
  useEffect(() => {
    if (isPanning || isEdgeCreating) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isPanning, isEdgeCreating, handleMouseMove, handleMouseUp]);

  /**
   * Handle anchor mouse down to start edge creation
   */
  const handleAnchorMouseDown = useCallback((nodeId, anchor, point) => {
    if (readOnly) return;
    startEdgeCreation(nodeId, anchor, point);
  }, [readOnly, startEdgeCreation]);

  /**
   * Handle anchor hover for target detection
   */
  const handleAnchorMouseEnter = useCallback((nodeId, anchor) => {
    setTargetHover(nodeId, anchor);
  }, [setTargetHover]);

  /**
   * Handle anchor hover end
   */
  const handleAnchorMouseLeave = useCallback(() => {
    clearTargetHover();
  }, [clearTargetHover]);

  /**
   * Get the source anchor point for preview line
   */
  const getSourcePoint = useCallback(() => {
    if (!edgeSourceNodeId) return null;
    const sourceNode = getNodeById?.(edgeSourceNodeId);
    if (!sourceNode) return null;
    return getNodeAnchor(sourceNode, edgeSourceAnchor);
  }, [edgeSourceNodeId, edgeSourceAnchor, getNodeById]);

  /**
   * Calculate transform for viewport
   */
  const viewportTransform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;

  return (
    <div
      ref={containerRef}
      className="arch-canvas"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onClick={handleCanvasClick}
      style={{
        cursor: isPanning ? 'grabbing' : 'default',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: '500px'
      }}
    >
      {/* Viewport container with pan/zoom transform */}
      <div
        className="arch-canvas__viewport"
        style={{
          transform: viewportTransform,
          transformOrigin: '0 0',
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          position: 'relative'
        }}
      >
        {/* Grid background */}
        <svg
          className="arch-canvas__grid"
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none'
          }}
        >
          <defs>
            <pattern
              id="arch-grid-pattern"
              width={GRID_SIZE}
              height={GRID_SIZE}
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx={GRID_SIZE / 2}
                cy={GRID_SIZE / 2}
                r="1"
                fill="var(--border-color, #2d3548)"
                opacity="0.3"
              />
            </pattern>
          </defs>
          <rect
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            fill="url(#arch-grid-pattern)"
          />
        </svg>

        {/* Edges (SVG layer) */}
        <svg
          className="arch-canvas__edges"
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'visible',
            pointerEvents: 'none'
          }}
        >
          <g style={{ pointerEvents: 'auto' }}>
            {edges.map(edge => {
              const sourceNode = getNodeById?.(edge.sourceId);
              const targetNode = getNodeById?.(edge.targetId);

              return (
                <ArchitectureLink
                  key={edge.id}
                  edge={edge}
                  sourceNode={sourceNode}
                  targetNode={targetNode}
                  isSelected={selectedEdgeId === edge.id}
                  onSelect={(id) => {
                    onSelectEdge?.(id);
                    onSelectNode?.(null);
                  }}
                  onDelete={onDeleteEdge}
                />
              );
            })}
          </g>

          {/* Preview line while creating edge */}
          {isEdgeCreating && previewPoint && (() => {
            const sourcePoint = getSourcePoint();
            if (!sourcePoint) return null;
            return (
              <g className="arch-canvas__edge-preview" style={{ pointerEvents: 'none' }}>
                <line
                  x1={sourcePoint.x}
                  y1={sourcePoint.y}
                  x2={previewPoint.x}
                  y2={previewPoint.y}
                  stroke={hoveredTarget ? '#10b981' : '#3b82f6'}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                />
                <circle
                  cx={previewPoint.x}
                  cy={previewPoint.y}
                  r={hoveredTarget ? 8 : 5}
                  fill={hoveredTarget ? '#10b981' : '#3b82f6'}
                  opacity={0.8}
                />
              </g>
            );
          })()}
        </svg>

        {/* Nodes (HTML layer for better text rendering and interaction) */}
        <div
          className="arch-canvas__nodes"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT
          }}
        >
          {nodes.map(node => {
            const isHighlighted = isNodeHighlighted?.(node) ?? false;
            // Get ALL vendors covering this category for split borders
            const vendorCoverage = isHighlighted ? (getNodeCoverage?.(node) || []) : [];
            // Dim non-highlighted nodes when overlay is active
            const isDimmed = hasVendorOverlay && !isHighlighted && !node.isInput && !node.isOutput;
            // Check if this node is a valid edge target
            const isValidTarget = isEdgeCreating && hoveredTarget?.nodeId === node.id;

            return (
              <ArchitectureNode
                key={node.id}
                node={node}
                isSelected={selectedNodeId === node.id}
                onSelect={(id) => {
                  onSelectNode?.(id);
                  onSelectEdge?.(null);
                }}
                onDelete={onDeleteNode}
                onOpenSelector={onOpenSelector}
                readOnly={readOnly}
                isHighlighted={isHighlighted}
                vendorCoverage={vendorCoverage}
                isDimmed={isDimmed}
                onAnchorMouseDown={handleAnchorMouseDown}
                onAnchorMouseEnter={handleAnchorMouseEnter}
                onAnchorMouseLeave={handleAnchorMouseLeave}
                isEdgeCreating={isEdgeCreating}
                isValidTarget={isValidTarget}
              />
            );
          })}
        </div>
      </div>

      {/* Zoom controls */}
      <div className="arch-canvas__controls">
        <button
          className="arch-canvas__control-btn"
          onClick={() => onZoom?.(Math.min(MAX_ZOOM, viewport.zoom + 0.1))}
          title="Zoom in"
          disabled={readOnly}
        >
          +
        </button>
        <span className="arch-canvas__zoom-level">
          {Math.round(viewport.zoom * 100)}%
        </span>
        <button
          className="arch-canvas__control-btn"
          onClick={() => onZoom?.(Math.max(MIN_ZOOM, viewport.zoom - 0.1))}
          title="Zoom out"
          disabled={readOnly}
        >
          -
        </button>
        <button
          className="arch-canvas__control-btn arch-canvas__control-btn--fit"
          onClick={() => {
            onZoom?.(1);
            onPan?.(-viewport.x, -viewport.y);
          }}
          title="Reset view"
          disabled={readOnly}
        >
          Fit
        </button>
      </div>
    </div>
  );
}

export default ArchitectureCanvas;
